import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { isNewPlan } from "../shared/utils";
import { CHANGE_IMAGE_OPACITY } from "../tools/actions/ActionHelper";
import { IMAGE_ACCEPT, IMAGE_FORMATS_HINT } from "../../lib/image_upload";
import Modal from "./Modal";

export default class ImageMenu extends AbstractProvidedClass {
    action = null;

    constructor(provider) {
        super(provider);

        this.container = this.p.createDiv()
            .parent('#p5canvas')
            .class('planner-toolbar-vert');


        this.heading = this.p.createElement('label', "Image")
            .parent(this.container)
            .class('input-label');

        this.imageManagementContainer = this.p.createDiv()
            .parent(this.container);

        this.createShowImageCheckbox();
        this.createAdjustModes();
        this.createOpacitySlider();
        this.createDeleteButton();

        // If it is a new plan, we need the user to save the plan
        // before uploading an image so we can properly link the
        // image to the plan.
        if (isNewPlan(this.p)) {
            this.container.style('width', '10rem');
            this.p.createElement('p', 'save plan before you can upload image')
                .parent(this.container);
            this.p.createButton('Save')
                .mousePressed(() => this.dataHandler.savePlan(true))
                .parent(this.container)
                .class('btn-secondary mar-r-sm');
        } else {
            this.createImageInput();
            this.createDeleteModal();
        }

        this.imageManagementContainer.hide();

        this.hide();
    }

    createShowImageCheckbox() {
        this.showImageOption = this.p.createDiv()
            .class('input-group')
            .parent(this.imageManagementContainer);
        this.showImageCheckbox = this.p.createCheckbox('Show', false)
            .id('show-image')
            .parent(this.showImageOption)
            .class('input-label');
        this.showImageCheckbox.changed(() => {
            this.settings.showImage = this.showImageCheckbox.checked();
        });
    }

    createAdjustModes() {
        this.adjustModesContainer = this.p.createDiv()
            .parent(this.imageManagementContainer)
            .class('mar-t-sm');
        this.adjustModes = this.p.createRadio('image-mode-radio')
            .parent(this.adjustModesContainer);
        this.adjustModes.option('move', 'Move');
        this.adjustModes.option('rotate', 'Rotate');
        this.adjustModes.option('scale', 'Scale');
        this.adjustModes.selected('move');

        let options = this.adjustModes.elt.children;
        for (let i = 0; i < options.length; i++) {
            let option = options[i];
            option.classList.add('mode-selector-option');
            option.children[1].remove();
            let icon = SVG().addTo(option);
            icon.addClass('icon');
            if (i === 0) {
                option.setAttribute('data-tooltip', 'Move (M)');
                icon.use('drag-move-2-fill', '/icons/__remixicon.sprite.svg?v=491');
            } else if (i === 1) {
                option.setAttribute('data-tooltip', 'Rotate (R)');
                icon.use('clockwise-line', '/icons/__remixicon.sprite.svg?v=491');
            } else if (i === 2) {
                option.setAttribute('data-tooltip', 'Scale (Q)');
                icon.use('ruler-line', '/icons/__remixicon.sprite.svg?v=491');
            }
        }
    }

    createOpacitySlider() {
        this.opacityContainer = this.p.createDiv()
            .parent(this.imageManagementContainer)
            .class('input-group');
        this.opacityLabel = this.p.createDiv('Opacity')
            .parent(this.opacityContainer)
            .class('input-label');
        this.opacitySlider = this.p.createSlider(0, 255, 255)
            .parent(this.opacityContainer)
            .mousePressed(() => this.action = this.actions.new(CHANGE_IMAGE_OPACITY, this.opacitySlider))
            .input(() => this.action.update())
            .mouseReleased(() => {
                this.action.finish();
                this.action = null;
            });
    }

    createDeleteButton() {
        this.deleteButton = this.p.createElement('a')
            .parent(this.imageManagementContainer)
            .mousePressed(() => {
                this.deleteModal.show();
            });
        let icon = SVG().addTo(this.deleteButton.elt);
        icon.addClass('btn-icon-clear icon-dark');
        icon.use('delete-bin-line', '/icons/__remixicon.sprite.svg?v=491');
    }

    createImageInput() {
        this.imageInputContainer = this.p.createDiv()
            .parent(this.container)
            .class('input-group');
        this.imageInputLabel = this.p.createElement('Label', 'Upload')
            .parent(this.imageInputContainer)
            .class('input-label');
        this.imageInputBtn = this.p.createElement('label')
            .class('btn-upload')
            .parent(this.imageInputContainer);
        this.imageInputBtnLabel = this.p.createElement('a')
            .class('btn-icon -full')
            .parent(this.imageInputBtn)
            .attribute('data-tooltip', 'Upload image');
        this.imageUploadIcon = SVG().addTo(this.imageInputBtnLabel.elt);
        this.imageUploadIcon.use('upload-cloud-line', '/icons/__remixicon.sprite.svg?v=491');
        this.imageUploadIcon.addClass('icon')
            .addClass('icon-gray');
        this.imageInput = this.p.createFileInput((file) => this.handleFile(file))
            .parent(this.imageInputBtn)
            .attribute('accept', IMAGE_ACCEPT);

        this.p.createElement('p', IMAGE_FORMATS_HINT)
            .parent(this.imageInputContainer)
            .class('input-description');

        this.imageLoading = SVG().addTo(this.imageInputBtnLabel.elt);
        this.imageLoading.use('loader-4-line', '/icons/__remixicon.sprite.svg?v=491');
        this.imageLoading.addClass('icon')
            .addClass('icon-gray')
            .addClass('rotate-animation');
        this.imageLoading.hide();
    }

    handleFile(file) {
        this.loading();
        this.dataHandler.updateImage(file);
    }

    loading() {
        this.imageUploadIcon?.hide();
        this.imageLoading?.show();
        this.setControlsEnabled(false);
    }

    // Restore the upload control and re-enable the inputs. On a rejected upload
    // pass imageLoaded=false so the management container stays hidden, since no
    // image loaded.
    loadingFinished(imageLoaded = true) {
        imageLoaded
            ? this.imageManagementContainer.show()
            : this.imageManagementContainer.hide();
        this.imageUploadIcon?.show();
        this.setControlsEnabled(true);
        this.imageLoading?.hide();
    }

    setControlsEnabled(enabled) {
        this.showImageCheckbox.elt.firstChild.firstChild.disabled = !enabled;
        enabled
            ? this.opacitySlider.removeAttribute('disabled')
            : this.opacitySlider.attribute('disabled', true);
        let options = this.adjustModes.elt.children;
        for (let i = 0; i < options.length; i++) {
            enabled
                ? options[i].firstChild.removeAttribute('disabled')
                : options[i].firstChild.setAttribute('disabled', true);
        }
    }

    deleteImage() {
        this.dataHandler.deleteImage(this.image.id);
        this.image = null;
        this.loadingFinished(false);
        this.deleteModal.hide();
        this.actions.removeImageActions();
        this.changesMade();
    }

    activate() {
        this.provider.image && !isNewPlan(this.p)
            ? this.imageManagementContainer.show()
            : this.imageManagementContainer.hide();
        this.show();
    }

    deactivate() {
        this.hide();
    }

    show() {
        this.container.show();
    }

    hide() {
        this.container.hide();
    }

    createDeleteModal() {
        this.deleteModal = new Modal(this.provider);
        this.deleteModal.setTitle('Are you sure you want to remove the image?')
            .addContent(this.p.createP('This action cannot be undone.'))
            .setButtonText('Remove')
            .onClickButton(() => this.deleteImage())
            .onClickCancel(() => this.deleteModal.hide())
            .onClickOutside(() => this.deleteModal.hide());

        this.deleteModal.hide();
    }
}
